import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { X, ChevronLeft, ChevronRight, ZoomIn, Images } from "lucide-react";

const images = Array.from({ length: 34 }, (_, i) => ({
  id: i,
  src: `/ecole/${i}.jpg`,
  alt: `École Kemkian — photo ${i + 1}`,
}));

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  if (index === null) return null;
  const img = images[index];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Counter */}
        <div className="absolute top-4 left-4 z-10 text-white/70 text-sm font-body">
          {index + 1} / {images.length}
        </div>

        {/* Prev */}
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Image */}
        <motion.img
          key={index}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          src={img.src}
          alt={img.alt}
          className="max-h-[85vh] max-w-[85vw] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Next */}
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronRight size={24} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

const KemkianGallery = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const displayed = showAll ? images : images.slice(0, 12);

  const handlePrev = () =>
    setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const handleNext = () =>
    setLightboxIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  // Keyboard navigation
  React.useEffect(() => {
    const onKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") setLightboxIndex(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex]);

  return (
    <section id="gallery-kemkian" className="section scroll-mt-20 bg-dark-soft">
      <div className="container mx-auto">

        {/* Header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4 text-secondary">
            Galerie — Kemkian
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6 text-white">
            La crèche et l'école en images
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead text-white/60">
            Sarh, quartier Kemkian — chaque photo raconte une histoire de vie transformée.
          </motion.p>
        </motion.div>

        {/* Gallery grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3">
          {displayed.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px -60px 0px" }}
              transition={{ duration: 0.4, delay: Math.min(i % 12, 8) * 0.05 }}
              className="relative group overflow-hidden rounded-xl cursor-pointer bg-gray-800"
              style={{ aspectRatio: i % 7 === 0 ? "1/1.3" : "1/1" }}
              onClick={() => setLightboxIndex(i)}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                <ZoomIn
                  size={28}
                  className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100"
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Show more */}
        {!showAll && images.length > 12 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-center mt-10"
          >
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white/80 hover:text-white hover:border-white/40 font-semibold text-sm px-8 py-3.5 rounded-full transition-all duration-300"
            >
              <Images size={16} />
              Voir les {images.length - 12} autres photos
            </button>
          </motion.div>
        )}

      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </section>
  );
};

export default KemkianGallery;
